import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useInView } from '@/hooks/useInView';
import {
  ArrowRight, ArrowDown, Check, Cpu, Wifi, BarChart3, Activity, Zap, Radio,
} from 'lucide-react';

const RED = '#E02D45';
const BLUE = '#38BDF8';
const GREEN = '#34D399';

/* ═══════════════════════════════════════════════════════
   SHARED BUILDING BLOCKS
   ═══════════════════════════════════════════════════════ */

function Section({ children, className = '', id }) {
  const [ref, inView] = useInView();
  return (
    <section ref={ref} id={id} className={`${inView ? 'animate-fade-in-up' : 'opacity-0'} ${className}`}>
      {children}
    </section>
  );
}

function TypewriterLine({ text, visible, startDelay = 0, highlightColor = null }) {
  const words = text.split(' ');
  let charIndex = 0;
  return (
    <div className="overflow-hidden">
      <span className="inline">
        {words.map((word, wi) => (
          <span key={wi} className="inline-block whitespace-nowrap">
            {word.split('').map((char, ci) => {
              const idx = charIndex++;
              return (
                <span key={ci} className={`inline-block typewriter-char ${visible ? 'typewriter-char-visible' : ''}`}
                  style={{ transitionDelay: `${startDelay + idx * 70}ms`, color: highlightColor || undefined }}>
                  {char}
                </span>
              );
            })}
            {wi < words.length - 1 && (() => { charIndex++; return (
              <span className={`inline-block typewriter-char ${visible ? 'typewriter-char-visible' : ''}`}
                style={{ transitionDelay: `${startDelay + (charIndex - 1) * 70}ms`, color: highlightColor || undefined }}>
                {'\u00A0'}
              </span>
            ); })()}
          </span>
        ))}
      </span>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   INDUSTRY PAGE TEMPLATE
   ═══════════════════════════════════════════════════════ */

export default function IndustryPageTemplate({ data }) {
  const [heroVisible, setHeroVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setHeroVisible(true), 500);
    return () => clearTimeout(t);
  }, []);

  return (
    <div>
      {/* 1. HERO */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 bg-[#060606] overflow-hidden" data-testid={`industry-hero-${data.slug}`}>
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: `linear-gradient(${RED}30 1px, transparent 1px), linear-gradient(90deg, ${RED}30 1px, transparent 1px)`,
          backgroundSize: '80px 80px'
        }} />
        <div className="absolute top-0 right-0 w-[600px] h-[400px] rounded-full blur-[200px]" style={{ backgroundColor: `${RED}06` }} />

        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 relative z-10">
          <div className={`transition-all duration-[1200ms] ease-out ${heroVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '100ms' }}>
            <div className="flex items-center gap-3 mb-8">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: RED }} />
              <p className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: RED }}>Mushin for {data.industry}</p>
            </div>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-[4.5rem] font-black uppercase tracking-[-0.03em] text-white leading-[0.92] max-w-4xl" data-testid="industry-heading">
            <TypewriterLine text={data.heroLine1} visible={heroVisible} startDelay={400} />
            <TypewriterLine text={data.heroLine2} visible={heroVisible} startDelay={400 + data.heroLine1.length * 70 + 200} highlightColor={RED} />
          </h1>

          <div className={`transition-all duration-[1400ms] ease-out ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: `${400 + (data.heroLine1.length + data.heroLine2.length) * 70 + 800}ms` }}>
            <div className="mt-10 h-[3px] w-16" style={{ backgroundColor: RED }} />
            <p className="mt-8 text-lg text-white/50 max-w-2xl leading-[1.7] font-medium">{data.heroSub}</p>
          </div>

          <div className={`mt-10 flex flex-wrap gap-4 transition-all duration-[1400ms] ease-out ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: `${400 + (data.heroLine1.length + data.heroLine2.length) * 70 + 1200}ms` }}>
            <Link to="/contact" className="text-white px-8 py-3.5 rounded-full font-bold text-sm inline-flex items-center gap-2 transition-all duration-300 hover:shadow-xl" style={{ backgroundColor: RED }} data-testid="industry-demo-cta">
              Book a Demo <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        <div className={`absolute bottom-6 left-1/2 -translate-x-1/2 transition-all duration-1000 ${heroVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '5000ms' }}>
          <ArrowDown className="w-5 h-5 text-white/15 animate-bounce" />
        </div>
      </section>

      {/* 2. WHY MUSHIN FOR THIS INDUSTRY */}
      <Section className="py-20 lg:py-28 bg-[#0A0A0A] border-t border-white/[0.04]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.25em] mb-6" style={{ color: RED }}>Why Mushin</p>
              <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-[-0.02em] text-white leading-[1.05]">
                {data.whyTitle}
              </h2>
              <p className="mt-6 text-base text-white/45 leading-[1.7] font-medium max-w-lg">{data.whyDesc}</p>
              <ul className="mt-8 space-y-3">
                {data.whyBullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                    <span className="text-sm text-white/55 font-medium">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            {/* Pain Points Cards */}
            <div className="grid grid-cols-2 gap-3">
              {data.painPoints.map((p, i) => (
                <div key={i} className="p-5 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-3" style={{ backgroundColor: `${RED}12`, border: `1px solid ${RED}20` }}>
                    <p.icon className="w-4 h-4" style={{ color: RED }} strokeWidth={1.7} />
                  </div>
                  <p className="text-sm font-bold text-white/80">{p.title}</p>
                  <p className="mt-1 text-[12px] text-white/30 leading-relaxed">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* 3. STANDARDS / COMPLIANCE */}
      <Section className="py-20 lg:py-28 bg-[#060606] border-t border-white/[0.04]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl mb-14">
            <p className="text-sm font-bold uppercase tracking-[0.25em] mb-6" style={{ color: RED }}>Standards & Compliance</p>
            <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-[-0.02em] text-white leading-[1.05]">
              {data.standardsTitle}
            </h2>
            <p className="mt-5 text-base text-white/40 max-w-xl leading-[1.7] font-medium">{data.standardsDesc}</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {data.standards.map((s, i) => (
              <div key={i} className="p-4 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:border-[#E02D45]/20 transition-all duration-300" data-testid={`standard-${i}`}>
                <p className="text-sm font-black text-white/80 uppercase">{s.name}</p>
                <p className="mt-1.5 text-[12px] text-white/30 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 p-5 rounded-xl bg-white/[0.02] border border-white/[0.05]">
            <p className="text-sm text-white/45 font-medium leading-[1.7]">
              <span className="font-bold text-white/70">Mushin digitizes the execution of these standards.</span> {data.standardsCTA}
            </p>
          </div>
        </div>
      </Section>

      {/* 4. MOST RELEVANT FACTORY OS APPS */}
      <Section className="py-20 lg:py-28 bg-[#0A0A0A] border-t border-white/[0.04]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl mb-14">
            <p className="text-sm font-bold uppercase tracking-[0.25em] mb-6" style={{ color: RED }}>Factory OS for {data.industry}</p>
            <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-[-0.02em] text-white leading-[1.05]">
              The Apps That Matter Most
            </h2>
            <p className="mt-5 text-base text-white/40 max-w-xl leading-[1.7] font-medium">
              Mushin Factory OS is modular. Here are the apps most critical for {data.industry.toLowerCase()} plant operations.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.factoryApps.map((app, i) => {
              const Icon = app.icon;
              return (
                <Link key={i} to={`/products/factory-os#${app.slug}`} className="block p-5 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:border-[#E02D45]/20 transition-all duration-300 hover:-translate-y-0.5" data-testid={`factory-app-${i}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center border" style={{ backgroundColor: `${RED}10`, borderColor: `${RED}20` }}>
                      <Icon className="w-[18px] h-[18px]" style={{ color: RED }} strokeWidth={1.7} />
                    </div>
                    <h3 className="text-sm font-bold text-white/80 uppercase">{app.name}</h3>
                  </div>
                  <p className="text-[13px] text-white/35 leading-relaxed font-medium">{app.relevance}</p>
                </Link>
              );
            })}
          </div>

          <div className="mt-10 text-center">
            <Link to="/products/factory-os#apps" className="text-sm font-bold inline-flex items-center gap-2 transition-colors hover:opacity-80" style={{ color: RED }}>
              Explore all Factory OS apps <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </Section>

      {/* 5. VENDOR CONNECT RELEVANCE */}
      <Section className="py-20 lg:py-28 bg-[#060606] border-t border-white/[0.04]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.25em] mb-6" style={{ color: BLUE }}>Vendor Connect for {data.industry}</p>
              <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-[-0.02em] text-white leading-[1.05]">
                {data.vendorTitle}
              </h2>
              <p className="mt-6 text-base text-white/45 leading-[1.7] font-medium max-w-lg">{data.vendorDesc}</p>
              <div className="mt-10">
                <Link to="/products/vendor-connect#vendor-apps" className="text-sm font-bold inline-flex items-center gap-2 transition-colors hover:opacity-80" style={{ color: BLUE }}>
                  Explore Vendor Connect <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {data.vendorApps.map((v, i) => (
                <Link key={i} to={`/products/vendor-connect#${v.slug}`} className="block p-4 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:border-[#38BDF8]/20 transition-all duration-300 hover:-translate-y-0.5">
                  <p className="text-sm font-bold text-white/80">{v.name}</p>
                  <p className="mt-1 text-[12px] text-white/30 leading-relaxed">{v.desc}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* 6. INDUSTRY 4.0 READY */}
      <Section className="py-24 lg:py-36 bg-[#0A0A0A] border-t border-white/[0.04]" data-testid="industry-4-section">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="text-center mb-16">
            <p className="text-sm font-bold uppercase tracking-[0.25em] mb-6" style={{ color: GREEN }}>Industry 4.0 Ready</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-[-0.02em] text-white leading-[1]">
              Not Just Quality.<br />Industry 4.0 Execution, Built In.
            </h2>
            <p className="mt-5 text-base text-white/40 max-w-2xl mx-auto font-medium leading-[1.7]">
              Mushin does not stop at compliance and quality systems. The same platform connects your machines, sensors, and shopfloor into a live digital execution layer.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { icon: Cpu, label: 'IoT / PLC Data', desc: 'Machine-connected production intelligence' },
              { icon: Activity, label: 'Real-Time OEE', desc: 'Availability, performance, and quality rate' },
              { icon: Radio, label: 'Andon Alerts', desc: 'Live shopfloor status and escalation' },
              { icon: BarChart3, label: 'Live Dashboards', desc: 'Plant-wide visibility in real time' },
              { icon: Zap, label: 'Energy Tracking', desc: 'Consumption, cost, and efficiency' },
              { icon: Wifi, label: 'Connected Ops', desc: 'Sensors, barcodes, and biometrics' },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="p-4 rounded-xl bg-white/[0.03] border border-white/[0.06] text-center hover:border-[#34D399]/20 transition-all duration-300">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: `${GREEN}10`, border: `1px solid ${GREEN}20` }}>
                    <Icon className="w-5 h-5" style={{ color: GREEN }} strokeWidth={1.7} />
                  </div>
                  <p className="text-[13px] font-bold text-white/80">{item.label}</p>
                  <p className="mt-1 text-[11px] text-white/25 leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>

          <p className="mt-10 text-center text-sm text-white/30 font-medium max-w-xl mx-auto">
            {data.industry4Desc}
          </p>
        </div>
      </Section>

      {/* 7. FINAL CTA */}
      <section className="py-28 lg:py-40 relative overflow-hidden bg-[#060606] border-t border-white/[0.04]" data-testid="industry-cta">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full blur-[150px]" style={{ backgroundColor: `${RED}06` }} />
        <div className="max-w-4xl mx-auto px-6 sm:px-10 lg:px-16 text-center relative z-10">
          <p className="text-sm font-bold uppercase tracking-[0.25em] mb-8" style={{ color: RED }}>Get Started</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-[-0.02em] text-white leading-[1]">
            {data.ctaTitle}
          </h2>
          <p className="mt-6 text-white/35 text-lg max-w-lg mx-auto font-medium leading-[1.7]">{data.ctaSub}</p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link to="/contact" className="text-white px-10 py-4 rounded-full font-bold text-base inline-flex items-center gap-2.5 transition-all duration-300 hover:shadow-xl" style={{ backgroundColor: RED }} data-testid="industry-final-cta">
              Book a Demo <ArrowRight className="w-5 h-5" />
            </Link>
            <a href="mailto:sales@mushinlabs.com" className="border-2 border-white/15 hover:border-white/30 text-white/45 hover:text-white px-10 py-4 rounded-full font-bold text-base transition-all duration-300">
              Talk to Sales
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
